import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as fontkitNs from "@pdf-lib/fontkit";
import { PDFDocument, rgb } from "pdf-lib";
import {
  activeQuoteItems,
  getQuoteCommercialTotals,
  quoteLineBase,
  quoteLineDiscountAmount,
  quoteLineNetBeforeGst,
} from "./quoteLineMath";
import { formatInr } from "./formatInr";
import { addRemarksAndFilesSection, loadQuoteAttachmentPdfBytes } from "./quoteRemarksAndFiles";
import { LAST_PAGE_1_B64 } from "./quotePDFAssets";

const fontkit = fontkitNs.default ?? fontkitNs;
const A4_WIDTH = 210;
const A4_HEIGHT = 297;
const BRAND_PLUM = [46, 26, 70];
const ELECTRICAL_HEADER_PLUM = [42, 26, 66];
const SECTION_HIGHLIGHT = [151, 103, 147];
const TABLE_BORDER = [219, 224, 235];
const BODY_TEXT = [31, 41, 55];
const MUTED_TEXT = [107, 114, 128];
const DESCRIPTION_TEXT = [86, 96, 112];
const APPENDIX_PAGE_OFFSETS_Y = [0, 0];
const TABLE_LEFT = 12;
const TABLE_RIGHT = 12;
const TABLE_WIDTH = A4_WIDTH - TABLE_LEFT - TABLE_RIGHT;
const MAIN_COL_WIDTHS = [14, 56, 38, 20, 20, 38];
const MAIN_TOTAL_COL_WIDTH = MAIN_COL_WIDTHS[5];
const SUMMARY_COL_WIDTHS = [56, 20, 38, 32, 40];
const ADDON_COL_WIDTHS = [12, 48, 20, 16, 34, 24, 32];
const ELECTRICAL_COL_WIDTHS = [12, 40, 26, 20, 34, 22, 32];
const SPACE_HEADING_FONT_SIZE = 10.8;
const TWO_BHK_SECTION_ORDER = [
  "entrance",
  "living room",
  "kitchen",
  "master bedroom 1",
  "other",
  "guest room",
  "guest bedroom",
  "children's bedroom",
  "childrens bedroom",
  "master bedroom 2",
  "parents room",
  "mandir room",
  "bathroom",
  "balcony",
];

const ASSETS = {
  firstPagePdf: "/proposal-first-page.pdf",
  background: "/pdf-bg.jpg",
  montserratRegular: "/fonts/Montserrat-Regular.ttf",
  lastPages: [
    LAST_PAGE_1_B64,
  ],
};

const COVER_TEXT = {
  fontSize: 9.4,
  valueX: 61.2,
  valueWidth: 36,
  cardFill: rgb(0.965, 0.965, 0.972),
  clearColumnX: 59.7,
  clearColumnBottomY: 99.9,
  clearColumnWidth: 38,
  clearColumnHeight: 46.4,
  proposalForY: 60.2,
  proposalNoY: 69,
  proposalDateY: 78.1,
  consultantY: 87.3,
  contactY: 96.7,
};

const mmToPt = (mm) => (mm * 72) / 25.4;
const coverPoint = (page, xMm, yMmFromTop) => {
  const { height } = page.getSize();
  return { x: mmToPt(xMm), y: height - mmToPt(yMmFromTop) };
};

const loadImageAsBase64 = (url) => {
  if (typeof url === "string" && url.startsWith("data:")) {
    return Promise.resolve(url);
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg", 0.96));
    };
    img.onerror = () => reject(new Error(`Could not load PDF asset: ${url}`));
    img.src = `${url}${url.includes("?") ? "&" : "?"}v=${Date.now()}`;
  });
};

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return window.btoa(binary);
};

const loadFontAsBase64 = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Could not load PDF font: ${url}`);
  return arrayBufferToBase64(await res.arrayBuffer());
};

const loadBytes = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Could not load PDF asset: ${url}`);
  return await res.arrayBuffer();
};

const registerFonts = (doc, montserratRegular) => {
  doc.addFileToVFS("Montserrat-Regular.ttf", montserratRegular);
  doc.addFont("Montserrat-Regular.ttf", "Montserrat", "normal");
  doc.addFont("Montserrat-Regular.ttf", "Montserrat", "bold");
};

const safeText = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
};

const asNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const getClientName = (quote) =>
  safeText(
    quote?.client?.name ||
      quote?.clientName ||
      quote?.project?.client?.name ||
      quote?.project?.clientName,
    "Client"
  );

const getProposalDate = (quote) => {
  const value = quote?.createdAt || quote?.updatedAt || new Date();
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
};

const getQuoteContact = (quote) =>
  safeText(
    quote?.createdBy?.phone ||
      quote?.createdBy?.mobile ||
      quote?.salesContact ||
      quote?.consultantContact,
    "+91 777 4017 137"
  );

const getSalesConsultant = (quote) =>
  safeText(
    quote?.createdBy?.name ||
      quote?.salesConsultant ||
      quote?.consultantName,
    "Tatsavi Spaces"
  );

const getProposalNumber = (quote) => safeText(quote?.quoteCode || quote?._id, "TS Proposal");

const getItemName = (item) =>
  safeText(item?.itemName || item?.name || item?.item || item?.title || item?.materialMaster?.itemName, "-");

const getDescription = (item) =>
  safeText(
    item?.lineDescription ||
      item?.itemDetails ||
      item?.description ||
      item?.materialMaster?.description ||
      item?.material ||
      item?.category,
    "-"
  );

const getMaterial = (item) => {
  if (isElectricalWork(item)) return "Approx. Points";
  return safeText(item?.category || item?.quoteMaterial || item?.materialMaster?.quoteMaterial, "-");
};

const getItemNameWithDescription = (item) => {
  const name = getItemName(item);
  const description = getDescription(item);
  if (!description || description === "-" || description === getMaterial(item)) return name;
  return `${name}\r\n${description}`;
};

const getItemNameParts = (item) => {
  const name = getItemName(item);
  const description = getDescription(item);
  return {
    name,
    description: !description || description === "-" || description === getMaterial(item) ? "" : description,
  };
};

const getAmount = (item) => {
  const saved = asNumber(item?.amount, NaN);
  return Number.isFinite(saved) ? saved : quoteLineNetBeforeGst(item);
};

const getItemTableAmount = (item) => quoteLineBase(item);

const formatMeasure = (value) => {
  const n = asNumber(value, NaN);
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const getSectionName = (item) =>
  safeText(
    item?.space ||
      item?.section ||
      item?.Space ||
      item?.defaultSpace ||
      item?.room ||
      item?.area ||
      item?.item?.defaultSpace,
    ""
  );

const normalizedSectionName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const isTwoBhkQuote = (quote) => {
  const text = `${quote?.bhkType || ""} ${quote?.title || ""} ${quote?.templatePdfPath || ""}`.toLowerCase();
  return /\b2\s*bhk\b/.test(text) || text.includes("2bhk");
};

const isOneBhkQuote = (quote) => {
  const text = `${quote?.bhkType || ""} ${quote?.title || ""} ${quote?.templatePdfPath || ""}`.toLowerCase();
  return /\b1\s*bhk\b/.test(text) || text.includes("1bhk");
};

const getPdfSectionName = (item, quote = null) => {
  const section = getSectionName(item);
  if (isOneBhkQuote(quote) && /^master bedroom(?:\s+\d+)?$/i.test(section)) {
    return "Master Bedroom";
  }
  if (/^master\s*bedroom\s*2$/i.test(section)) {
    return "Children's Bedroom";
  }
  return section;
};

const isElectricalSectionName = (value) => /(electrical|electric)(\s+work)?/i.test(String(value || ""));

const isElectricalWork = (item) => {
  // The saved section is authoritative. Item-name matching is only a fallback
  // for legacy lines that have no section; otherwise bedroom items such as an
  // LED strip get removed from their room table while still being counted in
  // that room's summary.
  const section = getPdfSectionName(item);
  if (section) return isElectricalSectionName(section);

  const name = `${getItemName(item)} ${safeText(item?.itemDetails || item?.description, "")}`;
  return /(amp socket|light point|builder point|led strip)/i.test(name);
};

const orderGroupsForQuote = (groups, quote) => {
  if (!isTwoBhkQuote(quote)) return groups;
  const rank = new Map(TWO_BHK_SECTION_ORDER.map((name, index) => [name, index]));
  return [...groups].sort((a, b) => {
    const aRank = rank.get(normalizedSectionName(a.name));
    const bRank = rank.get(normalizedSectionName(b.name));
    if (aRank !== undefined && bRank !== undefined) return aRank - bRank;
    if (aRank !== undefined) return -1;
    if (bRank !== undefined) return 1;
    return a.index - b.index;
  });
};

const groupItemsBySection = (items = [], quote = null) => {
  const groups = [];
  const byName = new Map();
  const seenOneBhkMasterItems = new Set();

  items.forEach((item) => {
    const name = getPdfSectionName(item, quote);
    if (isOneBhkQuote(quote) && /^master bedroom$/.test(normalizedSectionName(name))) {
      const itemKey = normalizedSectionName(getItemName(item));
      if (itemKey && seenOneBhkMasterItems.has(itemKey)) return;
      if (itemKey) seenOneBhkMasterItems.add(itemKey);
    }
    if (!byName.has(name)) {
      const group = { name, items: [], total: 0, index: groups.length };
      byName.set(name, group);
      groups.push(group);
    }
    const group = byName.get(name);
    group.items.push(item);
    group.total += getItemTableAmount(item);
  });

  return orderGroupsForQuote(groups, quote);
};

const buildQuotationRowsForGroups = (groups = []) => {
  const rows = [];

  groups.forEach((group) => {
    if (group.name) {
      rows.push([
        {
          content: group.name.toUpperCase(),
          colSpan: 6,
          styles: {
            fillColor: SECTION_HIGHLIGHT,
            textColor: [255, 255, 255],
            fontStyle: "bold",
            halign: "center",
            fontSize: SPACE_HEADING_FONT_SIZE,
            lineColor: SECTION_HIGHLIGHT,
            cellPadding: { top: 2, right: 2.4, bottom: 2, left: 2.4 },
          },
        },
      ]);
    }

    group.items.forEach((item, index) => {
      const amount = getItemTableAmount(item);
      const itemNameParts = getItemNameParts(item);
      rows.push([
        index + 1,
        {
          content: getItemNameWithDescription(item),
          quoteItemName: itemNameParts.name,
          quoteItemDescription: itemNameParts.description,
        },
        getMaterial(item),
        formatMeasure(item?.length),
        formatMeasure(item?.height),
        formatInr(amount),
      ]);
    });

    if (group.name && group.items.length) {
      rows.push([
        {
          content: `${group.name} Sub Total`,
          colSpan: 5,
          styles: { halign: "center", fontStyle: "bold", fillColor: false },
        },
        {
          content: formatInr(group.total),
          styles: { halign: "center", fontStyle: "bold", fillColor: false },
        },
      ]);
    }
  });

  return rows.length ? rows : [["-", "No items added", "-", "-", "-", "-"]];
};

const buildQuotationRows = (items = [], quote = null) =>
  buildQuotationRowsForGroups(groupItemsBySection(items, quote));

const quoteChargeDelta = (charge, itemsSubPreGst) => {
  const value = asNumber(charge?.value);
  const sign = charge?.option === "Debit" ? -1 : 1;
  if (charge?.type === "Percentage (%)") return sign * ((itemsSubPreGst * value) / 100);
  return sign * value;
};

const chargeLabel = (charge) =>
  safeText(charge?.title || charge?.label || charge?.name || charge?.description || "Additional Work");

const chargeAmount = (charge, itemsSubPreGst) => Math.abs(quoteChargeDelta(charge, itemsSubPreGst));

const moneyText = (value) => `₹ ${formatInr(value)}`;

const commonTableStyles = {
  font: "Montserrat",
  fontSize: 8,
  minCellHeight: 7,
  cellPadding: { top: 2.4, right: 2.6, bottom: 2.4, left: 2.6 },
  textColor: BODY_TEXT,
  lineColor: TABLE_BORDER,
  lineWidth: 0.16,
  valign: "middle",
  halign: "center",
  overflow: "linebreak",
  fillColor: false,
};

const commonHeadStyles = {
  fillColor: BRAND_PLUM,
  textColor: [255, 255, 255],
  fontStyle: "bold",
  halign: "center",
  valign: "middle",
  lineColor: BRAND_PLUM,
  lineWidth: 0.16,
  cellPadding: { top: 3, right: 2.8, bottom: 3, left: 2.8 },
};

const tablePageMargins = { top: 38, right: TABLE_RIGHT, bottom: 34, left: TABLE_LEFT };

const textLineHeightMm = (doc, fontSize, multiplier = 1.12) => (fontSize / doc.internal.scaleFactor) * multiplier;

const quoteItemNameCellMetrics = (doc, raw, availableWidth) => {
  const nameFontSize = 8.3;
  const descriptionFontSize = 6.4;
  const nameLines = doc.splitTextToSize(String(raw.quoteItemName || ""), availableWidth);
  const descriptionLines = raw.quoteItemDescription
    ? doc.splitTextToSize(String(raw.quoteItemDescription), availableWidth)
    : [];
  const nameLineHeight = textLineHeightMm(doc, nameFontSize, 1.0);
  const descriptionLineHeight = textLineHeightMm(doc, descriptionFontSize, 1.02);
  const gap = descriptionLines.length ? 0.3 : 0;

  return {
    nameFontSize,
    descriptionFontSize,
    nameLines,
    descriptionLines,
    nameLineHeight,
    descriptionLineHeight,
    gap,
  };
};

const drawQuoteItemNameCell = (doc, data) => {
  const raw = data.cell.raw || {};
  if (!raw.quoteItemName) return;

  const topPadding = data.cell.padding("top");
  const availableWidth = MAIN_COL_WIDTHS[1] - data.cell.padding("left") - data.cell.padding("right");
  const metrics = quoteItemNameCellMetrics(doc, raw, availableWidth);
  const x = data.cell.x + data.cell.width / 2;
  let y = data.cell.y + topPadding + metrics.nameLineHeight - 0.18;

  doc.setFont("Montserrat", "bold");
  doc.setFontSize(metrics.nameFontSize);
  doc.setTextColor(...BODY_TEXT);
  metrics.nameLines.forEach((line) => {
    doc.text(line, x, y, { align: "center" });
    doc.text(line, x + 0.08, y, { align: "center" });
    y += metrics.nameLineHeight;
  });

  if (metrics.descriptionLines.length) {
    y += metrics.gap;
    doc.setFont("Montserrat", "normal");
    doc.setFontSize(metrics.descriptionFontSize);
    doc.setTextColor(...DESCRIPTION_TEXT);
    metrics.descriptionLines.forEach((line) => {
      doc.text(line, x, y, { align: "center" });
      y += metrics.descriptionLineHeight;
    });
  }

  doc.setFont("Montserrat", "normal");
  doc.setFontSize(data.cell.styles.fontSize || 8);
  doc.setTextColor(...BODY_TEXT);
};

const drawMainHeaderCell = (doc, data) => {
  if (data.section !== "head") return;
  const raw = data.cell.raw || "";
  const text = String(typeof raw === "object" ? raw.content || "" : raw).trim();
  if (!text) return;

  const fontSize = data.cell.styles.fontSize || 8.8;
  const y = data.cell.y + data.cell.height / 2 + textLineHeightMm(doc, fontSize, 0.34);
  const x = data.cell.x + data.cell.width / 2;

  doc.setFont("Montserrat", "bold");
  doc.setFontSize(fontSize);
  doc.setTextColor(255, 255, 255);
  doc.text(text, x, y, { align: "center" });
  doc.text(text, x + 0.09, y, { align: "center" });
};

const buildSectionSummaryRows = (items = [], quote = null, hasDiscount = true) =>
  groupItemsBySection(items, quote).map((group) => {
    const baseTotal = group.items.reduce((sum, item) => sum + quoteLineBase(item), 0);
    const discount = group.items.reduce((sum, item) => sum + quoteLineDiscountAmount(item), 0);
    const subTotal = group.items.reduce((sum, item) => sum + quoteLineNetBeforeGst(item), 0);
    if (!hasDiscount) {
      return [
        group.name,
        group.items.length,
        moneyText(subTotal),
      ];
    }
    return [
      group.name,
      group.items.length,
      moneyText(baseTotal),
      moneyText(discount),
      moneyText(subTotal),
    ];
  });

const addAddOnTable = (doc, bgPage, quote, previousY) => {
  const itemsSubPreGst = (quote?.items || []).reduce((sum, item) => sum + getAmount(item), 0);
  const addOns = (quote?.charges || [])
    .filter((charge) => quoteChargeDelta(charge, itemsSubPreGst) > 0)
    .map((charge, index) => {
      const amount = chargeAmount(charge, itemsSubPreGst);
      return [
        index + 1,
        chargeLabel(charge),
        charge?.type === "Percentage (%)" ? `${formatInr(charge?.value || 0)}%` : "1",
        charge?.type === "Percentage (%)" ? "%" : "LS",
        charge?.type === "Percentage (%)" ? `On subtotal` : formatInr(amount),
        formatInr(amount),
        "",
      ];
    });

  if (!addOns.length) return previousY;

  const total = addOns.reduce((sum, row) => sum + Number(String(row[5]).replace(/,/g, "")), 0);
  addOns[addOns.length - 1][6] = formatInr(total);

  const startOnNewPage = previousY > 225;
  if (startOnNewPage) {
    doc.addPage();
    addFullPageImage(doc, bgPage);
  }

  autoTable(doc, {
    startY: startOnNewPage ? 26 : previousY + 12,
    margin: tablePageMargins,
    head: [
      [
        {
          content: "ADD-ON",
          colSpan: 7,
          styles: {
            fillColor: BRAND_PLUM,
            textColor: [255, 255, 255],
            fontStyle: "bold",
            halign: "center",
            fontSize: 9,
          },
        },
      ],
      ["Sr", "Work", "Qty. Approx", "Units", "Rate Per Nos/Mtr", "Amount", "Approximate Amount"],
    ],
    body: addOns,
    theme: "grid",
    pageBreak: "auto",
    rowPageBreak: "avoid",
    showHead: "everyPage",
    styles: {
      ...commonTableStyles,
      fontSize: 8,
    },
    headStyles: {
      ...commonHeadStyles,
    },
    alternateRowStyles: { fillColor: false },
    columnStyles: {
      0: { cellWidth: ADDON_COL_WIDTHS[0], halign: "center" },
      1: { cellWidth: ADDON_COL_WIDTHS[1], halign: "center" },
      2: { cellWidth: ADDON_COL_WIDTHS[2], halign: "center" },
      3: { cellWidth: ADDON_COL_WIDTHS[3], halign: "center" },
      4: { cellWidth: ADDON_COL_WIDTHS[4], halign: "center" },
      5: { cellWidth: ADDON_COL_WIDTHS[5], halign: "center" },
      6: { cellWidth: ADDON_COL_WIDTHS[6], halign: "center" },
    },
  });

  return doc.lastAutoTable?.finalY || previousY;
};

const electricalUnit = (item) => {
  const raw = safeText(item?.uom, "PT");
  if (/^nos$/i.test(raw)) return "PT";
  if (/^unit$/i.test(raw)) return "Meter";
  return raw;
};

const estimateElectricalTableHeight = (items = []) => {
  const headerRows = 2;
  const bodyRows = Math.max(1, items.length + 1);
  return headerRows * 9 + bodyRows * 9 + 8;
};

const estimateTotalsTableHeight = ({ discountTotal, additionalDiscount, chargeAdjustment }) => {
  const totalDiscount = (discountTotal || 0) + Math.abs(Math.min(0, chargeAdjustment || 0));
  const rows = 2 + (totalDiscount > 0 ? 1 : 0) + ((additionalDiscount || 0) > 0 ? 1 : 0);
  return rows * 8 + 8;
};

const ensureCommercialBlockFits = (doc, bgPage, previousY, { electricalItems, discountTotal, additionalDiscount = 0, chargeAdjustment }) => {
  if (!electricalItems.length) return previousY;

  const requiredHeight =
    estimateTotalsTableHeight({ discountTotal, additionalDiscount, chargeAdjustment }) +
    estimateElectricalTableHeight(electricalItems) +
    22;

  if (previousY + requiredHeight <= 262) return previousY;

  doc.addPage();
  addFullPageImage(doc, bgPage);
  return 34;
};

const addElectricalWorkTable = (doc, bgPage, items = [], previousY) => {
  if (!items.length) return previousY;

  const total = items.reduce((sum, item) => sum + getItemTableAmount(item), 0);
  const body = [
    [
      {
        content: "ELECTRICAL WORK and Other",
        colSpan: 7,
        styles: {
          fillColor: SECTION_HIGHLIGHT,
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "center",
          fontSize: SPACE_HEADING_FONT_SIZE,
        },
      },
    ],
    ...items.map((item, index) => {
    const row = [
      index + 1,
      getItemName(item),
      formatMeasure(item?.length),
      electricalUnit(item),
      formatInr(asNumber(item?.rate)),
      formatInr(getItemTableAmount(item)),
    ];
    if (index === 0) {
      row.push({
        content: formatInr(total),
        rowSpan: Math.max(1, items.length),
        styles: { halign: "center", valign: "middle", fontStyle: "bold" },
      });
    }
    return row;
  }),
  ];

  const requiredHeight = estimateElectricalTableHeight(items);
  const startOnNewPage = previousY + requiredHeight > 275;
  if (startOnNewPage) {
    doc.addPage();
    addFullPageImage(doc, bgPage);
  }

  autoTable(doc, {
    startY: startOnNewPage ? 26 : previousY + 12,
    margin: tablePageMargins,
    head: [
      [
        {
          content: "ADD-ON",
          colSpan: 7,
          styles: { fillColor: ELECTRICAL_HEADER_PLUM, textColor: [255, 255, 255], fontStyle: "bold", halign: "center", fontSize: SPACE_HEADING_FONT_SIZE },
        },
      ],
      ["Sr No", "Item", "Qty. Approx", "Units", "Rate", "Amount", "Approximate Amount"].map((content) => ({
        content,
        styles: {
          fillColor: ELECTRICAL_HEADER_PLUM,
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "center",
          lineColor: ELECTRICAL_HEADER_PLUM,
        },
      })),
    ],
    body,
    theme: "grid",
    pageBreak: "auto",
    rowPageBreak: "avoid",
    showHead: "everyPage",
    styles: {
      ...commonTableStyles,
      fontSize: 8.4,
      halign: "center",
    },
    headStyles: {
      ...commonHeadStyles,
      fillColor: false,
      textColor: BODY_TEXT,
      lineColor: TABLE_BORDER,
      halign: "center",
    },
    alternateRowStyles: { fillColor: false },
    columnStyles: {
      0: { cellWidth: ELECTRICAL_COL_WIDTHS[0], halign: "center" },
      1: { cellWidth: ELECTRICAL_COL_WIDTHS[1], halign: "center" },
      2: { cellWidth: ELECTRICAL_COL_WIDTHS[2], halign: "center" },
      3: { cellWidth: ELECTRICAL_COL_WIDTHS[3], halign: "center" },
      4: { cellWidth: ELECTRICAL_COL_WIDTHS[4], halign: "center" },
      5: { cellWidth: ELECTRICAL_COL_WIDTHS[5], halign: "center" },
      6: { cellWidth: ELECTRICAL_COL_WIDTHS[6], halign: "center" },
    },
    didParseCell: (data) => {
      if (data.section === "head" && data.row.index === 1) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.text = [""];
      }
    },
    didDrawCell: (data) => {
      if (data.section === "head" && data.row.index === 1) {
        drawMainHeaderCell(doc, data);
      }
    },
    willDrawPage: ({ pageNumber }) => {
      if (pageNumber > 1) addFullPageImage(doc, bgPage);
    },
  });

  return doc.lastAutoTable?.finalY || previousY;
};

const addTotalsTable = (doc, bgPage, previousY, { quote, items, baseTotal, discountTotal, additionalDiscount = 0, chargeAdjustment, quoteTotal, forceNewPage = false }) => {
  const totalDiscount = (discountTotal || 0) + Math.abs(Math.min(0, chargeAdjustment || 0));
  const hasDiscount = totalDiscount > 0;
  const body = buildSectionSummaryRows(items, quote, hasDiscount);

  const labelColWidth = 45;
  const valueColWidth = 35;
  const boxWidth = labelColWidth + valueColWidth;
  const rowHeight = 9;
  const showAdditionalDiscount = additionalDiscount > 0;
  const additionalDiscountLabel = String(
    quote?.additionalDiscountLabel ||
      (items || []).find((item) => item?._additionalDiscountLabel)?._additionalDiscountLabel ||
      "Additional Discount"
  ).trim() || "Additional Discount";

  const rowsToRender = [
    { label: "Sub Total:", value: moneyText(baseTotal) },
    ...(hasDiscount ? [{ label: "Discount:", value: moneyText(totalDiscount) }] : []),
    ...(showAdditionalDiscount ? [{ label: `${additionalDiscountLabel}:`, value: moneyText(additionalDiscount) }] : []),
    { label: "Total:", value: moneyText(quoteTotal), isTotal: true },
  ];

  const boxHeight = rowHeight * rowsToRender.length;
  const estimatedSummaryTableHeight = 8 + (body.length * 8.4);
  const totalCommercialHeight = estimatedSummaryTableHeight + 7 + boxHeight;

  // Keep section summary table and totals box together on the same page so totals is never stranded alone
  const startOnNewPage = forceNewPage || (previousY + totalCommercialHeight > 268);
  if (startOnNewPage) {
    doc.addPage();
    addFullPageImage(doc, bgPage);
  }

  const summaryHead = hasDiscount
    ? [["SECTION", "ITEMS", "BASE TOTAL", "DISCOUNT", "SUB TOTAL"]]
    : [["SECTION", "ITEMS", "TOTAL"]];

  const summaryWidths = hasDiscount
    ? [56, 20, 38, 32, 40]
    : [96, 40, 50];

  const columnStyles = {};
  summaryWidths.forEach((width, index) => {
    columnStyles[index] = { cellWidth: width, halign: "center" };
  });

  autoTable(doc, {
    startY: startOnNewPage ? 26 : previousY + 12,
    margin: tablePageMargins,
    head: summaryHead,
    body,
    theme: "grid",
    styles: {
      ...commonTableStyles,
      fontSize: 8.4,
      cellPadding: { top: 3, right: 3.2, bottom: 3, left: 3.2 },
      minCellHeight: 8.4,
      halign: "center",
    },
    headStyles: {
      ...commonHeadStyles,
      fontSize: 8.2,
      halign: "center",
      cellPadding: { top: 3.5, right: 3.2, bottom: 3.5, left: 3.2 },
    },
    alternateRowStyles: { fillColor: false },
    columnStyles,
    didParseCell: (data) => {
      if (data.section === "head") {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.text = [""];
      }
    },
    didDrawCell: (data) => {
      if (data.section === "head") {
        drawMainHeaderCell(doc, data);
      }
    },
  });

  const totalsBottomMargin = 16;
  let totalsTop = (doc.lastAutoTable?.finalY || previousY) + 7;

  if (totalsTop + boxHeight > A4_HEIGHT - totalsBottomMargin) {
    doc.addPage();
    addFullPageImage(doc, bgPage);
    totalsTop = 26;
  }

  const boxLeft = TABLE_LEFT + TABLE_WIDTH - boxWidth;
  const labelX = boxLeft + labelColWidth / 2;
  const valueX = boxLeft + labelColWidth + valueColWidth / 2;

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...TABLE_BORDER);
  doc.setLineWidth(0.16);
  doc.rect(boxLeft, totalsTop, boxWidth, boxHeight, "FD");

  for (let i = 1; i < rowsToRender.length; i++) {
    doc.line(boxLeft, totalsTop + rowHeight * i, boxLeft + boxWidth, totalsTop + rowHeight * i);
  }
  doc.line(boxLeft + labelColWidth, totalsTop, boxLeft + labelColWidth, totalsTop + boxHeight);

  rowsToRender.forEach((r, idx) => {
    const currentY = totalsTop + rowHeight * idx + 6.3;
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(r.isTotal ? 11 : 10.2);
    doc.setTextColor(...BODY_TEXT);
    doc.text(r.label, labelX, currentY, { align: "center" });
    doc.text(r.label, labelX + 0.08, currentY, { align: "center" });

    doc.text(r.value, valueX, currentY, { align: "center" });
    doc.text(r.value, valueX + 0.08, currentY, { align: "center" });
  });

  return totalsTop + boxHeight;
};

const addFullPageImage = (doc, image) => {
  doc.addImage(image, "JPEG", 0, 0, A4_WIDTH, A4_HEIGHT);
};

const dataUrlToBytes = (dataUrl) => {
  const base64 = String(dataUrl).includes(",") ? String(dataUrl).split(",")[1] : String(dataUrl);
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const addAppendixPagesToFinalPdf = async (finalPdf, lastPageDataUrls) => {
  for (let index = 0; index < lastPageDataUrls.length; index += 1) {
    const dataUrl = lastPageDataUrls[index];
    if (!dataUrl) continue;
    const bytes = dataUrlToBytes(dataUrl);
    const image = String(dataUrl).startsWith("data:image/png")
      ? await finalPdf.embedPng(bytes)
      : await finalPdf.embedJpg(bytes);
    const page = finalPdf.addPage([mmToPt(A4_WIDTH), mmToPt(A4_HEIGHT)]);
    const { width, height } = page.getSize();
    const offsetY = mmToPt(APPENDIX_PAGE_OFFSETS_Y[index] ?? 0);
    page.drawImage(image, { x: 0, y: offsetY, width, height });
  }
};

const addMainQuotationTable = (doc, bgPage, rows, startY = 43) => {
  autoTable(doc, {
    startY,
    margin: tablePageMargins,
    head: [["Sr No", "Item Name", "Material", "Length", "Height", "Total"]],
    body: rows,
    theme: "grid",
    pageBreak: "auto",
    rowPageBreak: "avoid",
    showHead: "everyPage",
    styles: {
      ...commonTableStyles,
      fontSize: 7.6,
      minCellHeight: 5.8,
      cellPadding: { top: 1.15, right: 2, bottom: 1.15, left: 2 },
    },
    headStyles: {
      ...commonHeadStyles,
      fontSize: 8.8,
      cellPadding: { top: 2.8, right: 2.4, bottom: 2.8, left: 2.4 },
    },
    alternateRowStyles: { fillColor: false },
    columnStyles: {
      0: { cellWidth: MAIN_COL_WIDTHS[0], halign: "center" },
      1: { cellWidth: MAIN_COL_WIDTHS[1], halign: "center", valign: "top", textColor: BODY_TEXT },
      2: { cellWidth: MAIN_COL_WIDTHS[2], halign: "center" },
      3: { cellWidth: MAIN_COL_WIDTHS[3], halign: "center" },
      4: { cellWidth: MAIN_COL_WIDTHS[4], halign: "center" },
      5: { cellWidth: MAIN_COL_WIDTHS[5], halign: "center" },
    },
    didParseCell: (data) => {
      if (data.section === "head" && data.row.index === 0) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.text = [""];
        return;
      }

      const raw = data.cell.raw || {};
      if (data.section !== "body" || data.column.index !== 1 || !raw.quoteItemName) return;

      const leftPadding = data.cell.padding("left");
      const rightPadding = data.cell.padding("right");
      const availableWidth = MAIN_COL_WIDTHS[1] - leftPadding - rightPadding;
      const metrics = quoteItemNameCellMetrics(doc, raw, availableWidth);
      data.cell.text = [""];
      data.cell.styles.minCellHeight =
        data.cell.padding("top") +
        data.cell.padding("bottom") +
        metrics.nameLines.length * metrics.nameLineHeight +
        metrics.gap +
        metrics.descriptionLines.length * metrics.descriptionLineHeight +
        0.35;
    },
    didDrawCell: (data) => {
      if (data.section === "head" && data.row.index === 0) {
        drawMainHeaderCell(doc, data);
      }
      if (data.section === "body" && data.column.index === 1) {
        drawQuoteItemNameCell(doc, data);
      }
    },
    willDrawPage: ({ pageNumber }) => {
      // Only pages created by autoTable need a background. Painting the first
      // page again can erase content previously drawn on that same PDF page.
      if (pageNumber > 1) addFullPageImage(doc, bgPage);
    },
  });

  return doc.lastAutoTable?.finalY || startY;
};

const printableQuoteItems = (quote) => quote?.items || [];

const drawCoverText = async (pdfDoc, page, quote, montserratBytes) => {
  pdfDoc.registerFontkit(fontkit);
  const font = await pdfDoc.embedFont(montserratBytes);
  const color = rgb(BRAND_PLUM[0] / 255, BRAND_PLUM[1] / 255, BRAND_PLUM[2] / 255);

  const clearValueColumn = () => {
    const point = coverPoint(page, COVER_TEXT.clearColumnX, COVER_TEXT.clearColumnBottomY);
    page.drawRectangle({
      x: point.x,
      y: point.y,
      width: mmToPt(COVER_TEXT.clearColumnWidth),
      height: mmToPt(COVER_TEXT.clearColumnHeight),
      color: COVER_TEXT.cardFill,
    });
  };

  const draw = (text, xMm, yMm, options = {}) => {
    const point = coverPoint(page, xMm, yMm);
    const textOptions = {
      ...point,
      size: options.size || COVER_TEXT.fontSize,
      font,
      color,
      maxWidth: options.maxWidth ? mmToPt(options.maxWidth) : undefined,
      lineHeight: options.lineHeight || options.size || COVER_TEXT.fontSize,
    };
    page.drawText(text, textOptions);
    if (options.bold) {
      page.drawText(text, { ...textOptions, x: textOptions.x + 0.22 });
      page.drawText(text, { ...textOptions, y: textOptions.y - 0.12 });
    }
  };

  clearValueColumn();

  [
    [getClientName(quote), COVER_TEXT.proposalForY],
    [getProposalNumber(quote), COVER_TEXT.proposalNoY],
    [getProposalDate(quote), COVER_TEXT.proposalDateY],
    [getSalesConsultant(quote), COVER_TEXT.consultantY],
    [getQuoteContact(quote), COVER_TEXT.contactY],
  ].forEach(([text, y]) => {
    draw(text, COVER_TEXT.valueX, y, { maxWidth: COVER_TEXT.valueWidth, bold: true });
  });
};

const addPackageComparisonSection = (doc, bgPage, quote, quoteTotal) => {
  doc.addPage();
  addFullPageImage(doc, bgPage);

  const activeTier = quote?.selectedTier || quote?.packageTier || quote?.items?.[0]?._selectedTier || 'signature';
  let signatureTotal = quoteTotal;
  let essentialTotal = Math.round(quoteTotal * 0.90);
  let prestigeTotal = Math.round(quoteTotal * 1.20);

  if (activeTier === 'essential') {
    signatureTotal = Math.round(quoteTotal / 0.90);
    essentialTotal = quoteTotal;
    prestigeTotal = Math.round(signatureTotal * 1.20);
  } else if (activeTier === 'prestige') {
    signatureTotal = Math.round(quoteTotal / 1.20);
    essentialTotal = Math.round(signatureTotal * 0.90);
    prestigeTotal = quoteTotal;
  }

  const TEXT_DARK_PLUM = [27, 18, 54];
  const CORAL_RED = [239, 90, 90];
  const MUTED_TEXT = [107, 114, 128];
  const BODY_TEXT = [45, 55, 72];

  // Header Title
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...TEXT_DARK_PLUM);
  doc.text("CHOOSE YOUR PACKAGE", 105, 26, { align: "center" });
  doc.text("CHOOSE YOUR PACKAGE", 105.12, 26, { align: "center" });

  // Subtitle
  doc.setFont("Montserrat", "normal");
  doc.setFontSize(8.2);
  doc.setTextColor(...TEXT_DARK_PLUM);
  doc.text("Same design, same scope, same layout. Only the material, hardware and", 105, 33, { align: "center" });
  doc.text("warranty change.", 105, 37.5, { align: "center" });

  // 3 Cards
  const cardWidth = 57;
  const cardHeight = 186;
  const cardTopY = 53;
  const cardXs = [13.5, 76.5, 139.5];

  const cardsData = [
    {
      tier: "ESSENTIAL",
      tagline: "Smart &\ndependable",
      plywood: "MR grade commercial\nplywood.",
      outerLaminate: "0.8 mm laminates from\n10+ handpicked\ncatalogues",
      innerLaminate: "White / off-white inner\nlaminate 0.8 mm.",
      hardware: "Onyx hinges and channels. 2\nYears warranty from Tatsavi.",
      warranty: "5 years.",
      total: essentialTotal,
      note: "(-10% Savings)",
      isPopular: false,
    },
    {
      tier: "SIGNATURE",
      tagline: "Our most chosen\npackage",
      plywood: "ISI 303 grade, 100% Gurjan face.\nBWR in dry areas, BWP in wet\nareas.",
      outerLaminate: "1 mm premium laminates\nfrom 15+ handpicked\ncatalogues\n(₹1,600 to 1,800 range).",
      innerLaminate: "Inner fabric laminate, 0.8 mm",
      hardware: "Onyx hinges and channels. 2\nYears warranty from Tatsavi.",
      warranty: "10 years.",
      total: signatureTotal,
      note: "(Standard Proposal)",
      isPopular: true,
    },
    {
      tier: "PRESTIGE",
      tagline: "Premium brands,\nlongest cover",
      plywood: "Greenply or Century. IS 710\ngrade in wet areas, IS 303\ngrade in dry areas.",
      outerLaminate: "1 mm laminate from top\nbrand catalogues like Merino,\nGreenlam, Royale Touche\n(₹2300 to 3000 range).",
      innerLaminate: "Premium 1 mm inner fabric\nlaminate.",
      hardware: "Hettich or Hafele hinges and\nchannels. Company warranty.",
      warranty: "15 years.",
      total: prestigeTotal,
      note: "(+20% Luxury Upgrade)",
      isPopular: false,
    },
  ];

  cardsData.forEach((c, idx) => {
    const x = cardXs[idx];
    const cx = x + cardWidth / 2;

    // Background and border
    if (c.isPopular) {
      doc.setDrawColor(...CORAL_RED);
      doc.setFillColor(255, 255, 255);
      doc.setLineWidth(0.45);
      doc.roundedRect(x, cardTopY, cardWidth, cardHeight, 4, 4, "FD");
    } else {
      doc.setDrawColor(215, 200, 222);
      doc.setFillColor(255, 255, 255);
      doc.setLineWidth(0.35);
      doc.roundedRect(x, cardTopY, cardWidth, cardHeight, 4, 4, "FD");
    }

    // Row divider lines
    const dividers = [77, 102, 132, 154, 180, 200];
    dividers.forEach((dy) => {
      doc.setDrawColor(c.isPopular ? 245 : 235, c.isPopular ? 220 : 226, c.isPopular ? 220 : 238);
      doc.setLineWidth(0.25);
      doc.line(x, dy, x + cardWidth, dy);
    });

    // Section 1: Header
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...TEXT_DARK_PLUM);
    doc.text(c.tier, cx, c.isPopular ? 65 : 63, { align: "center" });
    doc.text(c.tier, cx + 0.08, c.isPopular ? 65 : 63, { align: "center" });

    doc.setFont("Montserrat", "normal");
    doc.setFontSize(7.2);
    const tagLines = c.tagline.split("\n");
    tagLines.forEach((line, lIdx) => {
      doc.text(line, cx, (c.isPopular ? 70 : 69) + lIdx * 3.7, { align: "center" });
    });

    // Helper for item rows
    const drawRow = (label, content, labelY, contentStartY) => {
      doc.setFont("Montserrat", "bold");
      doc.setFontSize(7.4);
      doc.setTextColor(...CORAL_RED);
      doc.text(label, x + 4, labelY);
      doc.text(label, x + 4.08, labelY);

      doc.setFont("Montserrat", "normal");
      doc.setFontSize(6.8);
      doc.setTextColor(...BODY_TEXT);
      const lines = doc.splitTextToSize(content, cardWidth - 8);
      lines.forEach((line, lIdx) => {
        doc.text(line, x + 4, contentStartY + lIdx * 3.2);
      });
    };

    // Section 2: PLYWOOD
    drawRow("PLYWOOD:", c.plywood, 82.5, 86.5);

    // Section 3: OUTER LAMINATE
    drawRow("OUTER LAMINATE:", c.outerLaminate, 107.5, 111.5);

    // Section 4: INNER LAMINATE
    drawRow("INNER LAMINATE:", c.innerLaminate, 137.5, 141.5);

    // Section 5: HARDWARE
    drawRow("HARDWARE:", c.hardware, 159.5, 163.5);

    // Section 6: PLY WARRANTY
    drawRow("PLY WARRANTY:", c.warranty, 185.5, 189.5);

    // Section 7: TOTAL INVESTMENT
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(8.0);
    doc.setTextColor(...TEXT_DARK_PLUM);
    doc.text("TOTAL INVESTMENT", cx, 209, { align: "center" });
    doc.text("TOTAL INVESTMENT", cx + 0.08, 209, { align: "center" });

    doc.setFontSize(11.5);
    doc.setTextColor(...TEXT_DARK_PLUM);
    doc.text(`₹ ${formatInr(c.total)}`, cx, 219, { align: "center" });
    doc.text(`₹ ${formatInr(c.total)}`, cx + 0.1, 219, { align: "center" });

    doc.setFontSize(7.0);
    if (c.tier === "ESSENTIAL") doc.setTextColor(34, 139, 34);
    else if (c.tier === "SIGNATURE") doc.setTextColor(...CORAL_RED);
    else doc.setTextColor(110, 60, 140);
    doc.text(c.note, cx, 226, { align: "center" });

    // Pill badge on top of Signature card
    if (c.isPopular) {
      const pillW = 28;
      const pillH = 12;
      const pillX = cx - pillW / 2;
      const pillY = 46;

      doc.setDrawColor(...CORAL_RED);
      doc.setFillColor(...CORAL_RED);
      doc.roundedRect(pillX, pillY, pillW, pillH, 4.5, 4.5, "FD");

      doc.setFont("Montserrat", "bold");
      doc.setFontSize(6.5);
      doc.setTextColor(255, 255, 255);
      doc.text("MOST", cx, 51, { align: "center" });
      doc.text("MOST", cx + 0.08, 51, { align: "center" });
      doc.text("POPULAR", cx, 55.2, { align: "center" });
      doc.text("POPULAR", cx + 0.08, 55.2, { align: "center" });
    }
  });

  // Footer Notes
  doc.setFont("Montserrat", "normal");
  doc.setFontSize(7.2);
  doc.setTextColor(...TEXT_DARK_PLUM);
  doc.text("Scope, sizes and design are identical across all three packages.", 14, 248);
  doc.text("Prices are based on the measurements in this quotation. Final billing is on actual site", 14, 252.5);
  doc.text("measurements, with the existing terms and conditions applying.", 14, 256.5);

  // Minimalist sofa & floor lamp illustration
  doc.setDrawColor(...TEXT_DARK_PLUM);
  doc.setFillColor(...TEXT_DARK_PLUM);
  doc.setLineWidth(0.35);

  // Floor Lamp
  doc.line(166, 260, 172, 260);
  doc.line(169, 260, 169, 246);
  doc.line(169, 246, 174, 244);
  doc.line(174, 244, 177, 246);
  doc.roundedRect(175, 246, 4, 3, 1, 1, "FD");

  // Modern Sofa
  doc.roundedRect(174, 250, 21, 5, 1, 1, "FD");
  doc.roundedRect(173, 254, 23, 4, 1, 1, "FD");
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.3);
  doc.line(184.5, 250.5, 184.5, 257.5);
  doc.setFillColor(...TEXT_DARK_PLUM);
  doc.setDrawColor(...TEXT_DARK_PLUM);
  doc.roundedRect(172, 251, 2.5, 7, 1, 1, "FD");
  doc.roundedRect(194.5, 251, 2.5, 7, 1, 1, "FD");
  doc.setLineWidth(0.4);
  doc.line(174, 258, 172.5, 260.5);
  doc.line(195, 258, 196.5, 260.5);
};

const addTermsAndConditionsSection = (doc, bgPage) => {
  doc.addPage();
  addFullPageImage(doc, bgPage);

  let currentY = 28;

  // Payment Schedule
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(...BRAND_PLUM);
  doc.text("Payment Schedule:", TABLE_LEFT, currentY);
  currentY += 6.0;

  const paymentPoints = [
    { label: "10% – Design Initiation: ", desc: "Payable in advance to commence 2D drawings and 3D design work." },
    { label: "50% – Execution Commencement: ", desc: "Payable after design finalisation and before commencement of on-site execution, including advances and procurement." },
    { label: "35% – Basic Structure Completion: ", desc: "Payable immediately upon completion of basic carpentry structure and before procuring outer laminates, hardware." },
    { label: "5% – Handover: ", desc: "Payable upon completion and before handover of site." },
  ];

  doc.setFontSize(7.2);
  paymentPoints.forEach((p) => {
    doc.setFont("Montserrat", "bold");
    doc.setTextColor(...BODY_TEXT);
    doc.text("• ", TABLE_LEFT, currentY);
    doc.text(p.label, TABLE_LEFT + 3, currentY);
    const labelWidth = doc.getTextWidth(p.label);

    doc.setFont("Montserrat", "normal");
    const descLines = doc.splitTextToSize(p.desc, TABLE_WIDTH - 3 - labelWidth);
    if (descLines.length === 1) {
      doc.text(descLines[0], TABLE_LEFT + 3 + labelWidth, currentY);
      currentY += 4.0;
    } else {
      doc.text(descLines[0], TABLE_LEFT + 3 + labelWidth, currentY);
      currentY += 3.5;
      for (let i = 1; i < descLines.length; i += 1) {
        doc.text(descLines[i], TABLE_LEFT + 3, currentY);
        currentY += 3.5;
      }
      currentY += 0.8;
    }
  });

  currentY += 14.0;

  // Terms & Conditions
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(...BRAND_PLUM);
  doc.text("Terms & Conditions:", TABLE_LEFT, currentY);
  currentY += 6.0;

  const termsPoints = [
    "Quotation does not include Granite Top or any Civil work costing.",
    "Measurements mentioned are tentative and final billing will be based on actual site measurements.",
    "Items not mentioned in the quotation are not included in the scope of work.",
    "The above electric cost is tentative and will be calculated based on the final number of points.",
    "Electrical work only includes wire, sockets, buttons and labour charges.",
    "Any additional electric points or changes during execution will be charged extra.",
    "Any additional item, material or accessories added later will be billed separately.",
    "Painting work and wood polishing on MDF panelling, false ceiling, Pop panelling or ceiling surfaces is not included unless it is added separately.",
    "Assembly and Transportation included in Pune/PCMC Area.",
    "Wallpaper, curtains, sofa, mattress, light fixtures are a part of accessories, and are not included in quotation unless it is already added in quotation.",
    "Amount once paid are non refundable.",
    "The above quotation is tentative and it can vary as per design.",
    "Quotation validity is 15 days from the date of creation.",
    "Quotation may vary as per design 10%+-.",
    "The above quotation include designing and execution costs.",
  ];

  doc.setFont("Montserrat", "normal");
  doc.setFontSize(7.2);
  doc.setTextColor(...BODY_TEXT);

  // 2-column layout for clean, compact space management
  const colWidth = (TABLE_WIDTH - 6) / 2;
  const col1Points = termsPoints.slice(0, 8);
  const col2Points = termsPoints.slice(8);

  const startTermsY = currentY;
  let leftY = startTermsY;
  col1Points.forEach((text) => {
    doc.text("• ", TABLE_LEFT, leftY);
    const split = doc.splitTextToSize(text, colWidth - 4);
    split.forEach((line) => {
      doc.text(line, TABLE_LEFT + 3.2, leftY);
      leftY += 3.6;
    });
    leftY += 1.0;
  });

  let rightY = startTermsY;
  const rightColX = TABLE_LEFT + colWidth + 6;
  col2Points.forEach((text) => {
    doc.text("• ", rightColX, rightY);
    const split = doc.splitTextToSize(text, colWidth - 4);
    split.forEach((line) => {
      doc.text(line, rightColX + 3.2, rightY);
      rightY += 3.6;
    });
    rightY += 1.0;
  });

  return Math.max(leftY, rightY);
};

const addQuotationPages = async (doc, bgPage, quote) => {
  const items = activeQuoteItems(printableQuoteItems(quote));
  const omittedTemplateRows = items.length !== (quote?.items || []).length;
  const printableQuote = omittedTemplateRows ? { ...quote, items } : quote;
  const electricalItems = items.filter(isElectricalWork);
  const regularItems = items.filter((item) => !isElectricalWork(item));
  const totals = getQuoteCommercialTotals(omittedTemplateRows ? { ...quote, total: 0 } : quote, items);
  const baseTotal = totals.baseSubTotal;
  const effectiveDiscount = totals.discountTotal;
  const chargeAdjustment = totals.chargeAdjustment;
  const quoteTotal = totals.total;
  const groups = groupItemsBySection(regularItems, printableQuote);

  doc.addPage();
  addFullPageImage(doc, bgPage);

  doc.setFont("Montserrat", "bold");
  doc.setFontSize(17);
  doc.setTextColor(...BRAND_PLUM);
  doc.text("Quotation", 14, 28);

  doc.setFont("Montserrat", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED_TEXT);
  doc.text(`Client: ${getClientName(quote)}`, 14, 35);
  doc.text(`Date: ${getProposalDate(quote)}`, 154, 35);

  let finalY = 43;
  if (!(items.length && !regularItems.length && electricalItems.length)) {
    finalY = addMainQuotationTable(doc, bgPage, buildQuotationRowsForGroups(groups), 43);
  }

  finalY = addElectricalWorkTable(doc, bgPage, electricalItems, finalY);
  finalY = addAddOnTable(doc, bgPage, printableQuote, finalY);
  finalY = addTotalsTable(doc, bgPage, finalY, {
    quote: printableQuote,
    items,
    baseTotal,
    discountTotal: effectiveDiscount,
    additionalDiscount: totals.additionalDiscount,
    chargeAdjustment,
    quoteTotal,
    forceNewPage: false,
  });
  finalY = await addRemarksAndFilesSection(doc, bgPage, finalY, printableQuote);
  addPackageComparisonSection(doc, bgPage, printableQuote, quoteTotal);
  addTermsAndConditionsSection(doc, bgPage);
};

export const generateQuotePDFBlob = async (quote) => {
  const doc = new jsPDF("p", "mm", "a4");

  const [firstPagePdfBytes, bgPage, montserratRegular] = await Promise.all([
    loadBytes(ASSETS.firstPagePdf),
    loadImageAsBase64(ASSETS.background),
    loadFontAsBase64(ASSETS.montserratRegular),
  ]);
  registerFonts(doc, montserratRegular);

  await addQuotationPages(doc, bgPage, quote);
  doc.deletePage(1);

  const finalPdf = await PDFDocument.create();
  const [coverPage] = await finalPdf.embedPdf(firstPagePdfBytes, [0]);
  const firstPage = finalPdf.addPage([mmToPt(A4_WIDTH), mmToPt(A4_HEIGHT)]);
  firstPage.drawPage(coverPage, {
    x: 0,
    y: 0,
    width: mmToPt(A4_WIDTH),
    height: mmToPt(A4_HEIGHT),
  });
  const montserratBytes = await fetch(ASSETS.montserratRegular).then((res) => res.arrayBuffer());
  await drawCoverText(finalPdf, firstPage, quote, montserratBytes);

  const contentPdf = await PDFDocument.load(doc.output("arraybuffer"));
  (await finalPdf.copyPages(contentPdf, contentPdf.getPageIndices())).forEach((page) => finalPdf.addPage(page));

  const attachmentPdfBuffers = await loadQuoteAttachmentPdfBytes(quote);
  for (const buffer of attachmentPdfBuffers) {
    const attachmentPdf = await PDFDocument.load(buffer);
    (await finalPdf.copyPages(attachmentPdf, attachmentPdf.getPageIndices())).forEach((page) => finalPdf.addPage(page));
  }

  // Dynamic package comparison & terms/conditions pages are now generated directly in contentPdf

  const pdfBytes = await finalPdf.save();
  return new Blob([pdfBytes], { type: "application/pdf" });
};

export const generateQuotePDF = async (quote) => {
  const blob = await generateQuotePDFBlob(quote);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Quotation_${getClientName(quote).replace(/[\\/:*?"<>|]/g, "-")}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
